/**
 * CreditReportModal — مودال گزارش منابع اعتباری
 * دو تب:
 *   ۱. تراز منابع اعتباری  (داده‌های موجود گرید)
 *   ۲. ریز اسناد منبع اعتباری (از API)
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ui } from '../../components/ListPage';
import { jsonOf, netErr } from '../../utils/http';
import { ExcelModalButton } from '../../components/ExcelExportModal';

const toFa = n => String(n).replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const numFmt = n => (Number(n||0) === 0) ? '۰' : Number(n).toLocaleString('fa-IR');

const API = (path, opts={}) => fetch(`/api/accounting${path}`, {
  headers: { 'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

/* ════════════════════ تب ۱: تراز منابع ════════════════════ */
function BalanceTab({ data, year, dateInfo }) {
  // ستون‌های تراز (ثابت)
  const COLS = [
    { key: 'code',           label: 'کد' },
    { key: 'title',          label: 'عنوان' },
    { key: 'debit_before',   label: 'بدهکار(قبل)',    num: true },
    { key: 'credit_before',  label: 'بستانکار(قبل)',  num: true },
    { key: 'debit_current',  label: 'بدهکار(جاری)',   num: true },
    { key: 'credit_current', label: 'بستانکار(جاری)', num: true },
    { key: 'debit_total',    label: 'بدهکار(جمع)',    num: true },
    { key: 'credit_total',   label: 'بستانکار(جمع)',  num: true },
    { key: 'remain_debit',   label: 'مانده بدهکار',   num: true },
    { key: 'remain_credit',  label: 'مانده بستانکار', num: true },
  ];

  // جمع کل
  const totals = useMemo(() => {
    const t = {};
    COLS.filter(c => c.num).forEach(c => { t[c.key] = 0; });
    data.forEach(r => {
      COLS.filter(c => c.num).forEach(c => { t[c.key] += Number(r[c.key] || 0); });
    });
    return t;
  }, [data]);

  const subtitle = `سال مالی ${toFa(year)}` +
    (dateInfo?.from ? ` — ${toFa(dateInfo.from)} تا ${toFa(dateInfo.to)}` : '');

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
        <div style={{ fontSize:'13px', color:'#555' }}>{toFa(data.length)} منبع اعتباری</div>
        <ExcelModalButton
          title="تراز منابع اعتباری"
          subtitle={subtitle}
          columns={COLS}
          rows={data}
          style={{ padding:'4px 10px', fontSize:'11px' }}
        />
      </div>
      <div style={{ overflowX:'auto', maxHeight:'50vh' }}>
        <table style={{ ...tblBase, minWidth:'900px' }}>
          <thead style={{ position:'sticky', top:0, zIndex:5 }}>
            <tr>
              <th style={thB}>ردیف</th>
              {COLS.map(c => <th key={c.key} style={c.num ? thGreen : thB}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => (
              <tr key={r.id || i} style={{ background: i%2===0 ? '#fff' : '#fdf6e3' }}>
                <td style={{ ...tdBase, textAlign:'center', color:'#888' }}>{toFa(i+1)}</td>
                <td style={{ ...tdBase, textAlign:'center', fontWeight:'600' }}>{r.code}</td>
                <td style={{ ...tdBase, textAlign:'right' }}>{r.title}</td>
                {COLS.filter(c=>c.num).map(c => (
                  <td key={c.key} style={tdNum}>{numFmt(r[c.key])}</td>
                ))}
              </tr>
            ))}
            {/* سطر جمع */}
            <tr style={{ background:'#1a2f50' }}>
              <td style={{ ...tdBase, color:'#C8A84B', fontWeight:'700', textAlign:'center' }} colSpan={3}>جمع</td>
              {COLS.filter(c=>c.num).map(c => (
                <td key={c.key} style={{ ...tdNum, color:'#fff', fontWeight:'700', background:'#1a2f50' }}>{numFmt(totals[c.key])}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ════════════════════ تب ۲: ریز اسناد ════════════════════ */
function DetailTab({ creditId, creditTitle, year, by, rangeFrom, rangeTo }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState({});

  const load = useCallback(async () => {
    if (!creditId) return;
    setLoading(true); setError('');
    const p = new URLSearchParams({ year, by, from: rangeFrom, to: rangeTo });
    const j = await API(`/credits/${creditId}/document-details/?${p}`);
    setLoading(false);
    if (j.ok) {
      setRows(j.data);
      setInfo({ display: j.credit_display, sum_debit: j.sum_debit, sum_credit: j.sum_credit,
                remain_debit: j.remain_debit, remain_credit: j.remain_credit });
    } else setError(j.msg);
  }, [creditId, year, by, rangeFrom, rangeTo]);

  useEffect(() => { load(); }, [load]);

  const COLS = [
    { key: 'doc_no',      label: 'شماره سند' },
    { key: 'doc_date',    label: 'تاریخ سند' },
    { key: 'remark',      label: 'شرح' },
    { key: 'debit',       label: 'بدهکار',    num: true },
    { key: 'credit',      label: 'بستانکار',  num: true },
    { key: 'cheque_no',   label: 'شماره چک' },
    { key: 'cheque_date', label: 'تاریخ چک' },
  ];

  if (loading) return <div style={{ textAlign:'center', padding:'40px', color:'#888' }}>⏳ در حال بارگذاری...</div>;
  if (error) return <div style={{ ...ui.errBox, margin:'20px' }}>⚠️ {error}</div>;
  if (!creditId) return <div style={{ textAlign:'center', padding:'40px', color:'#888' }}>⬆ ابتدا یک منبع اعتباری انتخاب کنید</div>;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px', flexWrap:'wrap', gap:'8px' }}>
        <div>
          <div style={{ fontSize:'14px', fontWeight:'700', color:'#1a2f50' }}>ریز اسناد: {info.display || creditTitle}</div>
          <div style={{ fontSize:'12px', color:'#666', marginTop:'4px' }}>
            {toFa(rows.length)} ردیف
            {info.sum_debit > 0 && <> — جمع بدهکار: <b style={{color:'#1b5e20'}}>{numFmt(info.sum_debit)}</b></>}
            {info.sum_credit > 0 && <> — جمع بستانکار: <b style={{color:'#b71c1c'}}>{numFmt(info.sum_credit)}</b></>}
            {info.remain_debit > 0 && <> — مانده بدهکار: <b>{numFmt(info.remain_debit)}</b></>}
            {info.remain_credit > 0 && <> — مانده بستانکار: <b>{numFmt(info.remain_credit)}</b></>}
          </div>
        </div>
        <ExcelModalButton
          title={`ریز اسناد منبع اعتباری ${info.display || creditTitle}`}
          subtitle={`سال مالی ${toFa(year)}`}
          columns={COLS}
          rows={rows}
          style={{ padding:'4px 10px', fontSize:'11px' }}
        />
      </div>
      <div style={{ overflowX:'auto', maxHeight:'50vh' }}>
        <table style={{ ...tblBase, minWidth:'800px' }}>
          <thead style={{ position:'sticky', top:0, zIndex:5 }}>
            <tr>
              <th style={thB}>ردیف</th>
              {COLS.map(c => <th key={c.key} style={c.num ? thGreen : thB}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ background: r.remark === 'نقل از قبل' ? '#e3f2fd' : i%2===0 ? '#fff' : '#fdf6e3' }}>
                <td style={{ ...tdBase, textAlign:'center', color:'#888' }}>{r.remark === 'نقل از قبل' ? '' : toFa(i+1)}</td>
                <td style={{ ...tdBase, textAlign:'center' }}>{r.doc_no}</td>
                <td style={{ ...tdBase, textAlign:'center', direction:'ltr' }}>{r.doc_date}</td>
                <td style={{ ...tdBase, textAlign:'right', maxWidth:'260px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.remark}>
                  {r.remark === 'نقل از قبل' ? <b style={{color:'#0d47a1'}}>{r.remark}</b> : r.remark}
                </td>
                <td style={{ ...tdNum, color:'#1b5e20' }}>{numFmt(r.debit)}</td>
                <td style={{ ...tdNum, color:'#b71c1c' }}>{numFmt(r.credit)}</td>
                <td style={{ ...tdBase, textAlign:'center' }}>{r.cheque_no}</td>
                <td style={{ ...tdBase, textAlign:'center', direction:'ltr' }}>{r.cheque_date}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} style={{ padding:'30px', textAlign:'center', color:'#999' }}>سندی ثبت نشده</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ════════════════════ مودال اصلی ════════════════════ */
export default function CreditReportModal({ data, selected, year, dateInfo, by, rangeFrom, rangeTo, onClose }) {
  const [tab, setTab] = useState(selected ? 1 : 0); // 0=تراز، 1=ریز اسناد

  const tabStyle = idx => ({
    padding: '8px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
    border: 'none', borderBottom: tab === idx ? '3px solid #1a2f50' : '3px solid transparent',
    background: tab === idx ? '#fff' : '#f5f5f5',
    color: tab === idx ? '#1a2f50' : '#888',
    fontFamily: 'inherit', transition: 'all 0.2s',
  });

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* هدر */}
        <div style={header}>
          <div style={headerTitle}>🖨️ چاپ / گزارش — منابع اعتباری</div>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </div>

        {/* تب‌ها */}
        <div style={{ display:'flex', borderBottom:'1px solid #e0e0e0', background:'#fafafa' }}>
          <button onClick={() => setTab(0)} style={tabStyle(0)}>📊 تراز منابع</button>
          <button onClick={() => setTab(1)} style={tabStyle(1)} disabled={!selected}>
            📋 ریز اسناد {selected ? `(${selected.code})` : ''}
          </button>
        </div>

        {/* بدنه */}
        <div style={body}>
          {tab === 0 && <BalanceTab data={data} year={year} dateInfo={dateInfo} />}
          {tab === 1 && (
            <DetailTab
              creditId={selected?.id}
              creditTitle={selected ? `${selected.code} - ${selected.title}` : ''}
              year={year} by={by}
              rangeFrom={rangeFrom} rangeTo={rangeTo}
            />
          )}
        </div>

        {/* فوتر */}
        <div style={footer}>
          <button onClick={() => window.print()} style={btnPrint}>🖨️ چاپ</button>
          <button onClick={onClose} style={btnClose}>بستن</button>
        </div>
      </div>
    </div>
  );
}

/* ─── استایل‌ها ─── */
const overlay = {
  position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
  display:'flex', justifyContent:'center', alignItems:'center', zIndex:9999,
};
const modal = {
  background:'#fff', borderRadius:'14px', width:'95%', maxWidth:'1100px',
  maxHeight:'92vh', display:'flex', flexDirection:'column',
  boxShadow:'0 20px 60px rgba(0,0,0,0.3)', overflow:'hidden',
};
const header = {
  display:'flex', justifyContent:'space-between', alignItems:'center',
  padding:'14px 20px', background:'linear-gradient(135deg,#1a2f50,#0d1b2a)', color:'#fff',
};
const headerTitle = { fontSize:'15px', fontWeight:'700' };
const closeBtn = {
  background:'rgba(255,255,255,0.2)', border:'none', color:'#fff',
  width:'28px', height:'28px', borderRadius:'50%', fontSize:'14px',
  cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
};
const body = { flex:1, overflowY:'auto', padding:'16px 20px' };
const footer = {
  display:'flex', justifyContent:'flex-end', gap:'10px',
  padding:'12px 20px', borderTop:'1px solid #eee', background:'#fafafa',
};
const btnPrint = {
  padding:'8px 20px', border:'none', borderRadius:'8px',
  background:'#1a2f50', color:'#fff', fontSize:'13px', fontWeight:'700',
  cursor:'pointer', fontFamily:'inherit',
};
const btnClose = {
  padding:'8px 18px', border:'1px solid #ccc', borderRadius:'8px',
  background:'#fff', color:'#555', fontSize:'13px', fontWeight:'600',
  cursor:'pointer', fontFamily:'inherit',
};
const tblBase = {
  width:'100%', borderCollapse:'collapse', fontSize:'12px',
  fontFamily:"'Vazirmatn',Tahoma,sans-serif",
};
const thB = {
  padding:'8px 6px', background:'#1a2f50', color:'#fff', fontSize:'11px',
  fontWeight:'600', textAlign:'center', borderBottom:'2px solid #0d1b2a',
  whiteSpace:'nowrap', position:'sticky', top:0,
};
const thGreen = { ...thB, background:'#1b5e20' };
const tdBase = { padding:'6px 6px', fontSize:'12px', borderBottom:'1px solid #e8eaf6' };
const tdNum = {
  ...tdBase, direction:'ltr', textAlign:'left', fontWeight:'600',
  fontFamily:"'Vazirmatn',Tahoma,sans-serif",
};
